import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFirestore } from "../firebase/FirestoreContext";
import {
  getDocs,
  collection,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
} from "firebase/firestore";
import moment from "moment";
import { CSVLink } from "react-csv";
import { EnvContext } from "../../context/EnvContext";
import { createDeliveryMailConfirmation } from "../functions/sendingMail/sendingDeliveryMail"; // Import the function
import config from "../../config";
import { ToastContainer, toast } from "react-toastify";
import TablePagination from "@mui/material/TablePagination";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { logoBase64 } from "../../utils/logoBase64";

const Orders = () => {
  const environment = useContext(EnvContext);
  const { seller_id, id } = useParams();
  const db = useFirestore();
  const navigate = useNavigate();

  // Determine the base path based on the environment
  const collectionPath =
    environment === "staging"
      ? config.enveironment.staging.collectionPath
      : config.enveironment.production.collectionPath;

  //alert(collectionPath);

  const [products, setProducts] = useState([]);
  const [store, setStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedCorrectAddressId, setExpandedCorrectAddressId] =
    useState(null); // New state for expanded correct address
  const [currentStatus, setCurrentStatus] = useState("all"); // Default filter status
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState({});
  const [addressOrderCounts, setAddressOrderCounts] = useState({}); // { [orderId]: count }

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchOrders = async () => {
    if (!seller_id) return;
    setLoading(true);
    setProducts([]);
    try {
      const ordersCollectionRef = collection(
        db,
        `${collectionPath}Orders/OrdersList/DataBase`,
      );
      const q = query(
        ordersCollectionRef,
        where("seller_id", "==", seller_id),
        orderBy("created_at", "desc"),
      );
      const ordersDoc = await getDocs(q);

      const orders = ordersDoc.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        status: doc.data().status || "Not started", // Set default status to "Not started"
        items: doc.data().items.map((item) => ({
          ...item,
          notes: item.notes || "", // Ensure notes are initialized for each item
        })),
      }));

      setOrders(orders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async () => {
    setLoading(true);
    try {
      const storesCollectionRef = collection(
        db,
        `${collectionPath}Stores/StoresList/DataBase`,
      );
      const querySnapshot = await getDocs(storesCollectionRef);
      const stores = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      console.log("Fetched Stores:", stores);

      setStores(stores);
    } catch (error) {
      console.error("Error fetching stores:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (seller_id) {
      fetchOrders();
    } else {
      fetchStores();
    }
  }, [seller_id, db, collectionPath]);

  const handleNotesChange = (productId, value) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => ({
        ...order,
        items: order.items.map((item) =>
          item.id === productId ? { ...item, notes: value } : item,
        ),
      })),
    );
  };
  const downloadInvoice = async (order) => {
    const doc = new jsPDF();

    doc.addImage(logoBase64, "PNG", 10, 10, 45, 10); // Adjust position and size as needed

    // Title
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Fullproof Order Invoice", 10, 35);

    // Order Number
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Order No. ${order.sooperstock_order_id.split("-")[0]}`, 10, 45);

    // Table for Items
    const tableStartY = 60;
    const tableData = order.items.map((item) => [
      item.product_name,
      item.quantity,
      `$${item.final_price.toFixed(2)}`,
      `$${(item.quantity * item.final_price).toFixed(2)}`,
    ]);

    doc.autoTable({
      head: [["Item", "Qty", "Price", "Subtotal"]],
      body: tableData,
      startY: tableStartY,
      margin: { left: 10 },
      headStyles: { fillColor: [240, 240, 240], textColor: 0 },
      styles: { halign: "center" },
    });

    // Order Subtotal, Shipping, and Total
    const subtotal = order.items.reduce(
      (sum, item) => sum + item.quantity * item.final_price,
      0,
    );
    const shipping = order.shipping_cost || 0; // Default to 0 if no shipping cost
    const perishableAmount = order.perishableAmount || 0; // Default to 0 if no shipping cost
    const total = subtotal + shipping + perishableAmount;

    let finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`ORDER SUBTOTAL`, 130, finalY);
    doc.text(`$${subtotal.toFixed(2)}`, 180, finalY, { align: "right" });

    finalY += 5;
    doc.text(`Shipping`, 130, finalY);
    doc.text(`$${shipping.toFixed(2)}`, 180, finalY, { align: "right" });

    if (perishableAmount > 0) {
      finalY += 5;
      doc.setFontSize(8);
      doc.text(`+ Cold Shipping Fee`, 132, finalY);
      doc.setFontSize(10);
      doc.text(`$${perishableAmount.toFixed(2)}`, 180, finalY, {
        align: "right",
      });
    }

    finalY += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`ORDER TOTAL`, 130, finalY);
    doc.text(`$${total.toFixed(2)}`, 180, finalY, { align: "right" });

    // Footer Note
    finalY += 10;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      "THANK YOU FOR YOUR ORDER! PLEASE SHARE FULLPROOF WITH YOUR FRIENDS USING YOUR INVITE CODE TO EARN FREE SOOPER CASH TO SPEND ON YOUR FUTURE ORDERS!",
      10,
      finalY,
      { maxWidth: 190 },
    );

    // Save the PDF
    doc.save(`Invoice_${order.sooperstock_order_id}.pdf`);
  };

  //

  const saveChanges = async (e) => {
    e.preventDefault();
    setLoading(true);
    const listingRef = collection(
      db,
      `${collectionPath}Listings/ListingsList/DataBase`,
    );

    for (const product of products) {
      const docId = `${id}_${product.id}`;
      const listingDocRef = doc(listingRef, docId);
      const listingDoc = await getDoc(listingDocRef);

      if (!product.listing_price) {
        // then delete from the Listings/ListingsList/DataBase collection
        if (listingDoc.exists()) {
          await deleteDoc(listingDocRef);
        }
        continue;
      }

      // things from form that are empty can't be put in the database
      Object.keys(product).forEach((key) => {
        if (product[key] === null || product[key] === undefined) {
          delete product[key];
        }
      });

      // if the product is not available set seller_product_rating to 0
      if (product.listing_product_notAvailable) {
        product.seller_product_rating = 0;
      }

      const dataToSave = {
        ...product,
        _collection: "listing",
        seller_id: id,
        seller_name: store?.seller_name,
        seller_uid: store?.seller_uid,
        product_id: product.id,
        seller_product_id: docId,
      };

      // Remove product.id from dataToSave
      delete dataToSave.id;

      if (listingDoc.exists()) {
        await updateDoc(listingDocRef, dataToSave);
      } else {
        await setDoc(listingDocRef, dataToSave);
      }
    }

    setLoading(false);
  };

  const fetchAddressOrderCount = async (address, optionalAddress) => {
    if (!address) return 0;
    let normalizedAddress = address.replace(/,?\s*United States\s*$/i, "");
    if (optionalAddress && optionalAddress.trim()) {
      const parts = normalizedAddress.split(",");
      if (parts.length >= 2) {
        parts.splice(1, 0, " " + optionalAddress.trim());
        normalizedAddress = parts.join(",");
      } else {
        normalizedAddress = normalizedAddress + ", " + optionalAddress.trim();
      }
    }
    normalizedAddress = normalizedAddress.trim().toLowerCase();

    const ordersCollectionRef = collection(
      db,
      `${collectionPath}Orders/OrdersList/DataBase`,
    );
    const q = seller_id
      ? query(
          ordersCollectionRef,
          where("seller_id", "==", seller_id),
          orderBy("created_at", "desc"),
        )
      : query(ordersCollectionRef, orderBy("created_at", "desc"));
    const snapshot = await getDocs(q);
    let count = 0;
    snapshot.forEach((doc) => {
      const o = doc.data();
      let oAddress = o?.shipmentAddress?.address || "";
      let oOptional = o?.shipmentAddress?.address_optional || "";
      let full = oAddress.replace(/,?\s*United States\s*$/i, "");
      if (oOptional && oOptional.trim()) {
        const parts = full.split(",");
        if (parts.length >= 2) {
          parts.splice(1, 0, " " + oOptional.trim());
          full = parts.join(",");
        } else {
          full = full + ", " + oOptional.trim();
        }
      }
      full = full.trim().toLowerCase();
      if (full === normalizedAddress) count++;
    });
    return count;
  };

  const toggleDetails = async (orderId) => {
    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }
    setExpandedOrderId(orderId);
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    const address = order?.shipmentAddress?.address;
    const optional = order?.shipmentAddress?.address_optional;
    if (address && addressOrderCounts[orderId] === undefined) {
      const count = await fetchAddressOrderCount(address, optional);
      setAddressOrderCounts((prev) => ({ ...prev, [orderId]: count }));
    }
  };

  const changeItemStatus = async (productId, status) => {
    setLoading(true);
    const ordersCollectionRef = collection(
      db,
      `${collectionPath}Orders/OrdersList/DataBase`,
    );
    const orderDocRef = doc(ordersCollectionRef, expandedOrderId);
    const orderDoc = await getDoc(orderDocRef);

    if (orderDoc.exists()) {
      const orderData = orderDoc.data();
      const updatedItems = orderData.items.map((item) => {
        if (item.id === productId) {
          item.sending_status = status;
        }
        return item;
      });

      // Check if all items now have "Pick/pack" status
      const allItemsPicked = updatedItems.every(
        (item) => item.sending_status === "pick_pack",
      );

      // Update order status to "Preparing" if all items are "Pick/pack"
      const newStatus = allItemsPicked ? "Preparing" : orderData.status;

      await updateDoc(orderDocRef, { items: updatedItems, status: newStatus });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === expandedOrderId
            ? { ...order, items: updatedItems, status: newStatus }
            : order,
        ),
      );
    } else {
      console.error("Order not found with id:", productId);
    }

    setLoading(false);
  };

  const changeOrderStatus = async (orderId, status) => {
    setLoading(true);
    const ordersCollectionRef = collection(
      db,
      `${collectionPath}Orders/OrdersList/DataBase`,
    );

    const orderDocRef = doc(ordersCollectionRef, orderId);
    const orderDoc = await getDoc(orderDocRef);

    if (orderDoc.exists()) {
      await updateDoc(orderDocRef, { status });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId ? { ...order, status } : order,
        ),
      );
    } else {
      console.error("Order not found with id:", orderId);
    }

    setLoading(false);
  };

  const filteredOrders = orders.filter((order) => {
    // Extract the relevant fields and normalize them to lowercase
    const orderId = order.sooperstock_order_id?.toLowerCase() || "";
    const address = order?.shipmentAddress?.address?.toLowerCase() || "";
    const buyerEmail = order?.buyerEmail?.toLowerCase() || "";

    // Check if the search term matches any of the fields
    const matchesSearch =
      orderId.includes(searchTerm.toLowerCase()) ||
      address.includes(searchTerm.toLowerCase()) ||
      buyerEmail.includes(searchTerm.toLowerCase());

    const statusFilterValues = ["PAID", "shipped", "complete", "other", "pending"];
    const filterByStatus =
      statusFilterValues.includes(currentStatus) && currentStatus !== "all";
    let matchesStatus = true;
    if (filterByStatus) {
      if (currentStatus === "other") {
        const otherStatuses = ["canceled", "returned", "reviewing"];
        matchesStatus =
          otherStatuses.includes(order.status) ||
          !["PAID", "shipped", "complete", "pending"].includes(order.status);
      } else {
        matchesStatus = order.status === currentStatus;
      }
    }

    return matchesSearch && matchesStatus;
  });

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  //
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders((prevSelectedOrders) =>
      prevSelectedOrders.includes(orderId)
        ? prevSelectedOrders.filter((id) => id !== orderId)
        : [...prevSelectedOrders, orderId],
    );
  };
  //
  const saveProductNotes = async (orderId, productId, notes) => {
    setLoading(true);
    try {
      const ordersCollectionRef = collection(
        db,
        `${collectionPath}Orders/OrdersList/DataBase`,
      );
      const orderDocRef = doc(ordersCollectionRef, orderId);
      const orderDoc = await getDoc(orderDocRef);

      if (!orderDoc.exists()) {
        toast.error("Error: Order not found.");
        return;
      }

      const orderData = orderDoc.data();
      const updatedItems = orderData.items.map((item) => {
        if (item.id === productId) {
          return { ...item, notes: notes }; // Update the notes field with the provided value
        }
        return item;
      });

      await updateDoc(orderDocRef, { items: updatedItems }); // Save updated items back to Firestore
      toast.success("Notes updated successfully!");
    } catch (error) {
      console.error("Error saving notes:", error);
      toast.error("Error saving notes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  //
  const generateCsvData = () => {
    const csvData = [
      ["Order ID", "Name", "Address"], // CSV headers
      ...selectedOrders.map((orderId) => {
        const order = orders.find((o) => o.id === orderId);
        return [
          order.sooperstock_order_id,
          order?.shipmentAddress?.address_name,
          order?.shipmentAddress?.address,
          order?.shipmentAddress?.city,
          order?.shipmentAddress?.state,
          order?.shipmentAddress?.zip_code,
          order?.shipmentAddress?.country || "USA", // Default to USA if missing
        ];
      }),
    ];

    return csvData;
  };

  //
  const sendDeliveryConfirmation = async (order) => {
    const orderData = {
      user_id: order.user_id,
      buyer_address: order.buyer_address,
      buyerEmail: order.buyerEmail,
      sellerEmail: order.sellerEmail,
      isAnonymous: order.isAnonymous || false,
      shippingCost: order.shippingCost || 0,
      items: order.items || [],
      sooperstock_order_id: order.sooperstock_order_id,
      created_at: order?.created_at || new Date().toISOString(),
      seller_id: order.seller_id,
      shipmentAddress: order.shipmentAddress || {},
      tracking_number: order.tracking_number || "",
      tracking_carrier: order.tracking_carrier || "",
    };

    try {
      // Send the delivery confirmation email
      await createDeliveryMailConfirmation(db, orderData, "package_shipped");

      // Update Firestore with the email log
      const ordersCollectionRef = collection(
        db,
        `${collectionPath}Orders/OrdersList/DataBase`,
      );
      const orderDocRef = doc(ordersCollectionRef, order.id);

      await updateDoc(orderDocRef, {
        sendedMail: [
          ...(order.sendedMail || []), // Keep previous logs if they exist
          { mail_type: "package_shipped", date: new Date().toISOString() },
        ],
      });

      console.log("Delivery confirmation sent and log updated successfully.");
    } catch (error) {
      console.error("Error sending delivery confirmation:", error);
    }
  };

  const confirmManualPayment = async (order) => {
    if (
      !window.confirm(
        `Confirm payment received for Order ${order.sooperstock_order_id}?\n\nThis will mark the order as PAID and send the customer their order confirmation email.`,
      )
    )
      return;

    setLoading(true);
    try {
      const ordersCollectionRef = collection(
        db,
        `${collectionPath}Orders/OrdersList/DataBase`,
      );
      const orderDocRef = doc(ordersCollectionRef, order.id);

      await updateDoc(orderDocRef, {
        status: "PAID",
        payment_confirmed_at: new Date().toISOString(),
      });

      // Build order confirmation email (same structure as createOrderMailConfirmation.js)
      const items = order.items || [];
      let itemsHtml = "";
      items.forEach((item) => {
        const pricePerUnit = item.final_price || item.listing_price || 0;
        const linePrice = (pricePerUnit * item.quantity).toFixed(2);
        const imageUrl =
          item?.images && item.images.length > 0
            ? item.images[0]
            : "https://firebasestorage.googleapis.com/v0/b/fullproof-app.firebasestorage.app/o/Logos%2Fno_product_image.png?alt=media&token=54805bf7-c43a-4a75-b0ae-0f5e9330c724";
        itemsHtml += `
          <tr style="border-bottom:1px solid #ddd;">
            <td style="padding:10px; width:60px; height:60px; text-align:center; background-color:#f5f5f5;">
              <img src="${imageUrl}" alt="${item.product_name}" width="50" height="50" style="object-fit:cover;" />
            </td>
            <td style="padding:10px; text-align:left;">
              <p style="margin:0; font-size:14px;">${item.product_name}</p>
              <p style="margin:0; font-size:14px;">Qty: ${item.quantity}</p>
              <p style="margin:0; font-size:14px;">$${linePrice}</p>
            </td>
            <td style="padding:10px; text-align:right;"></td>
          </tr>
        `;
      });

      const totalDollars = (
        (order.paid_amount_total || 0) / 100
      ).toFixed(2);
      itemsHtml += `
        <tr>
          <td colspan="2" style="padding:10px; text-align:left; font-size:18px; font-weight:bold;">Total*</td>
          <td style="padding:10px; text-align:right; font-size:18px;">$${totalDollars}</td>
        </tr>
        <tr>
          <td colspan="2" style="padding:10px; text-align:left;">*Includes shipping and other applicable fees</td>
        </tr>
      `;

      const { address_name, city, state } = order.shipmentAddress || {};
      const isAnonymous = order.isAnonymous || false;
      const manageMessage = isAnonymous
        ? `<div style="text-align:left; margin-bottom:20px;">
            <a href="https://getfullproof.com/" target="_blank" style="display:inline-block;border:1px solid black;color:black;font-size:14px;padding:3px 8px;border-radius:100px;text-decoration:none;">Sign up</a>
            <span style="margin-left:10px; font-size:14px;">Next time to keep your order history!</span>
          </div>`
        : `<div style="text-align:center; margin-bottom:20px;">
            <a href="https://getfullproof.com/orderhistory" target="_blank" style="display:inline-block;background-color:#007aff;color:#ffffff;font-size:16px;font-weight:bold;padding:12px 0;border-radius:100px;text-align:center;text-decoration:none;width:100%;margin:20px 0;">View order</a>
          </div>`;

      const htmlContent = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Order Confirmation - Fullproof</title>
        <style>body{margin:0;padding:0;background-color:#fff;font-family:Arial,sans-serif;}table{border-collapse:collapse;width:100%;}a{text-decoration:none;color:inherit;}.footer{background-color:#000;color:#fff;padding:20px;}</style>
        </head><body style="margin:0 20px;padding:0;background-color:#ffffff;">
        <div style="max-width:600px;margin:0 auto;padding:20px 0;">
          <div style="text-align:center;"><img src="https://firebasestorage.googleapis.com/v0/b/fullproof-app.firebasestorage.app/o/Logos%2Ffullproof_logo_black.png?alt=media&token=bf0aba5e-179e-4e2b-89fc-8ca39512a5bc" alt="Fullproof Logo" width="200" /></div>
          <div style="font-size:18px;font-weight:bold;margin:20px 0;text-align:center;">Thanks for your order!</div>
          <div style="padding:20px 0;border-bottom:1px solid #ddd;text-align:center;"><img src="https://firebasestorage.googleapis.com/v0/b/fullproof-app.firebasestorage.app/o/Logos%2Forder_confirmation.png?alt=media&token=790c668b-f03a-4090-8b5a-ad82c34c6b87" alt="Order Confirmation" width="100%" style="max-width:500px;" /></div>
          <div style="font-size:18px;font-weight:bold;margin:20px 0;text-align:left;">We're preparing your order now.</div>
          <div style="text-align:left;margin-bottom:10px;">${address_name || ""} - ${city || ""}, ${state || ""}</div>
          <div style="text-align:left;margin-bottom:20px;">Order#: ${order.sooperstock_order_id}</div>
          ${manageMessage}
          <table border="0" cellspacing="0" cellpadding="0" style="width:100%;margin:20px 0;">${itemsHtml}</table>
          <div class="footer">
            <p style="font-size:12px;line-height:1.6;margin:0;">©2025 Fullproof, Inc. or its affiliates.<br/>
              <a href="https://getfullproof.com/terms" target="_blank" style="color:#fff;text-decoration:underline;">Terms of Service</a> &amp;
              <a href="https://getfullproof.com/privacy" target="_blank" style="color:#fff;text-decoration:underline;">Privacy Policy</a>
            </p>
            <br/><img src="https://firebasestorage.googleapis.com/v0/b/fullproof-app.firebasestorage.app/o/Logos%2Ffullproof_logo_white.png?alt=media&token=3182d97d-add6-40ed-8790-da221c9cb2af" alt="Fullproof Footer Logo" style="width:110px;display:block;" />
          </div>
        </div></body></html>`;

      const textContent = `Your order was confirmed!\n\nOrder#: ${order.sooperstock_order_id}\n\nTotal: $${totalDollars}\n\nContinue shopping at https://getfullproof.com`;

      const emailData = {
        to: [order.buyerEmail],
        message: {
          html: htmlContent,
          text: textContent,
          subject: `Your Order Confirmation - Fullproof (Order ${order.sooperstock_order_id})`,
          from: "no-reply@getfullproof.com",
        },
        sooperstock_order_id: order.sooperstock_order_id,
        user_id: order.user_id,
        cart_id: order.cart_id,
        mail_type: "order_confirmation",
        isStaging: collectionPath !== "",
      };

      if (collectionPath === "") {
        emailData.bcc = [
          "steven@getfullproof.com",
          "ryan@getfullproof.com",
          "bookis@getfullproof.com",
          "chris@getfullproof.com",
        ];
      }

      await addDoc(collection(db, "Messages/Mail/DataBase"), emailData);

      await updateDoc(orderDocRef, {
        sendedMail: [
          ...(order.sendedMail || []),
          { mail_type: "order_confirmation", date: new Date().toISOString() },
        ],
      });

      setOrders((prevOrders) =>
        prevOrders.map((o) =>
          o.id === order.id ? { ...o, status: "PAID" } : o,
        ),
      );

      toast.success(
        `Payment confirmed for Order ${order.sooperstock_order_id}. Confirmation email sent.`,
      );
    } catch (error) {
      console.error("Error confirming manual payment:", error);
      toast.error("Failed to confirm payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Tracking code
  const handleTrackingNumberChange = (orderId, value) => {
    setTrackingNumbers((prev) => ({
      ...prev,
      [orderId]: value,
    }));
  };

  const saveTrackingNumber = async (orderId) => {
    setLoading(true);
    try {
      const ordersCollectionRef = collection(
        db,
        `${collectionPath}Orders/OrdersList/DataBase`,
      );
      const orderDocRef = doc(ordersCollectionRef, orderId);

      const trackingNumber = trackingNumbers[orderId] || "";
      const detectedCarrier = detectCarrier(trackingNumber); // Automatically determine the carrier

      await updateDoc(orderDocRef, {
        tracking_number: trackingNumber,
        tracking_carrier: detectedCarrier, // Save the detected carrier to Firestore
      });

      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                tracking_number: trackingNumber,
                tracking_carrier: detectedCarrier,
              }
            : order,
        ),
      );

      toast.success("Tracking information saved successfully!");
    } catch (error) {
      console.error("Error saving tracking information:", error);
      toast.error("Error saving tracking information. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getTrackingLink = (trackingNumber) => {
    if (!trackingNumber) return null;

    if (trackingNumber.startsWith("1Z")) {
      return `https://www.ups.com/track?tracknum=${trackingNumber}`;
    } else if (/^\d{12,15}$/.test(trackingNumber)) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
    } else if (/^\d{10,12}$/.test(trackingNumber)) {
      return `https://www.fedex.com/fedextrack/?tracknumbers=${trackingNumber}`;
    } else {
      return trackingNumber;
    }
  };

  const detectCarrier = (trackingNumber) => {
    if (!trackingNumber) return "";

    if (trackingNumber.startsWith("1Z")) {
      return "UPS";
    } else if (/^\d{12,15}$/.test(trackingNumber)) {
      return "USPS";
    } else if (/^\d{10,12}$/.test(trackingNumber)) {
      return "FedEx";
    } else {
      return "Unknown Carrier";
    }
  };

  // Pagination
  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to the first page
  };

  const correctAddress = (fullAddress, optionalAddress) => {
    if (!fullAddress) return "";

    let address = fullAddress.replace(/,?\s*United States\s*$/i, "");

    if (optionalAddress && optionalAddress.trim()) {
      const parts = address.split(",");
      if (parts.length >= 2) {
        parts.splice(1, 0, " " + optionalAddress.trim());
        address = parts.join(",");
      } else {
        address = address + ", " + optionalAddress.trim();
      }
    }
    return address.trim();
  };

  return (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />
      <h2>
        {environment === "staging" && (
          <span style={{ background: "#dae121", marginRight: 5 }}>Staging</span>
        )}
        Orders {store?.seller_name}
      </h2>{" "}
      {!seller_id ? (
        <form onSubmit={saveChanges} style={styles.form}>
          <div style={{ padding: "20px" }}>
            <div style={{ ...styles.formElement, width: "100%" }}>
              {stores.map((store) => (
                <div
                  key={store.id}
                  style={{
                    display: "flex",
                    padding: "10px 0",
                    borderBottom: ".5px solid silver",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/main/orders/${store.id}`)}
                >
                  <div style={{ width: "150px", color: "gray" }}>
                    {store.seller_uid}
                  </div>
                  <div>{store.seller_name}</div>
                </div>
              ))}
            </div>
          </div>
        </form>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              padding: "20px 0",
            }}
          >
            <div
              onClick={() => navigate(`/main/orders`)}
              style={{ padding: "20px 0", cursor: "pointer" }}
            >
              {"<"} Back to Stores
            </div>

            <div
              style={{ color: "black", padding: "20px 0", cursor: "pointer" }}
            >
              <CSVLink
                data={generateCsvData()}
                filename="usps_shipping_labels.csv"
                className="btn btn-primary"
                target="_blank"
              >
                Download Selected Orders as CSV
              </CSVLink>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <p>Filter by status:</p>
              <select
                value={currentStatus}
                onChange={(e) => {
                  setCurrentStatus(e.target.value);
                }}
                style={{
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #ccc",
                  marginLeft: "10px",
                }}
              >
                <option value="all">ALL</option>
                <option value="pending">Pending Payment</option>
                <option value="PAID">New order</option>
                <option value="shipped">In Transit</option>
                <option value="complete">Complete</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <form onSubmit={saveChanges} style={styles.form}>
            <div style={{ padding: "20px" }}>
              <div style={{ display: "flex", padding: "0px 0 10px 0" }}>
                <input
                  type="text"
                  placeholder="Search by Order ID or Address"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    padding: "10px",
                    width: "100%",
                    borderRadius: "5px",
                    border: "1px solid #ccc",
                  }}
                />
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  style={{
                    padding: "0px 20px",
                    marginLeft: 10,
                    backgroundColor: "gray",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "18px",
                    justifyContent: "center",
                    alignContent: "center",
                  }}
                >
                  &times;
                </button>
              </div>
              {filteredOrders.length === 0 ? (
                <div style={{ ...styles.td, textAlign: "center" }}>
                  No orders found
                </div>
              ) : (
                <div>
                  <table style={{ ...styles.table }}>
                    <thead>
                      <tr style={{ backgroundColor: "rgba(49,57,66,.02)" }}>
                        <th style={styles.th}></th>
                        <th style={styles.th}>Order ID</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Payment Type</th>
                        <th style={styles.th}>Latest Change</th>
                        <th style={styles.th}>Payment Date</th>
                        <th style={styles.th}>Open Details</th>
                        <th style={styles.th}>Order Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedOrders.map((order) => {
                        // switch status color by order status
                        let statusColor;
                        switch (order.status) {
                          case "pending":
                            statusColor = "#FFC107";
                            break;
                          case "in_progress":
                            statusColor = "orange";
                            break;
                          case "reviewing":
                            statusColor = "orange";
                            break;
                          case "shipped":
                            statusColor = "#478169";
                            break;
                          case "complete":
                            statusColor = "#4bdc62";
                            break;
                          case "canceled":
                            statusColor = "silver";
                            break;
                          case "escalate":
                            statusColor = "red";
                            break;
                          case "returned":
                            statusColor = "purple";
                            break;
                          default:
                            statusColor = "#c7bec0";
                        }
                        //
                        let paymentDate;
                        if (order?.paymentType === "Stripe") {
                          paymentDate = new Date(
                            order?.paymentDetails?.created * 1000,
                          ).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });
                        } else if (
                          order?.paymentType === "Wallet" ||
                          order?.paymentType === "Manual"
                        ) {
                          paymentDate = new Date(
                            order?.created_at?.seconds * 1000,
                          ).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });
                        } else {
                          paymentDate = new Date(
                            order?.paymentDetails?.created_at,
                          ).toLocaleString("en-US", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });
                        }

                        //
                        const createdAt = new Date(
                          order?.paymentDetails?.payment?.createdAt,
                        );
                        return (
                          <React.Fragment key={order.id}>
                            <tr
                              style={{
                                backgroundColor:
                                  expandedOrderId === order.id
                                    ? "rgba(49,57,66,.1)"
                                    : "rgba(49,57,66,.03)",
                                border: "1px solid white",
                              }}
                            >
                              {/* <p>
                                {JSON.stringify(order.status)}
                              </p> */}
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedOrders.includes(order.id)}
                                  onChange={() =>
                                    toggleOrderSelection(order.id)
                                  }
                                />
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                  fontWeight: "700",
                                  borderLeft: "5px solid",
                                  borderColor: statusColor,
                                }}
                              >
                                {order.sooperstock_order_id}
                              </td>
                              <td style={{ ...styles.td, display: "flex" }}>
                                <div
                                  style={{
                                    backgroundColor: statusColor,
                                    padding: "5px 10px",
                                    borderRadius: "25px",
                                    color: "white",
                                    fontSize: "11px",
                                    color: "black",
                                    fontWeight: 600,
                                  }}
                                >
                                  {order.status || "Not started"}
                                </div>
                              </td>
                              <td style={{ ...styles.td }}>
                                {order.paymentType === "Manual"
                                  ? order.manualPaymentMethod === "venmo"
                                    ? "Venmo"
                                    : "Zelle"
                                  : order.paymentType}
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                }}
                              >
                                {moment(
                                  order?.created_at
                                    ? order.created_at.toDate()
                                    : new Date(),
                                ).format("MM/DD/YY, h:mm a")}
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                  display: "flex",
                                }}
                              >
                                {paymentDate ? (
                                  <div
                                    style={{
                                      backgroundColor: "#4bdc62",
                                      padding: "5px 10px",
                                      borderRadius: "25px",
                                      color: "white",
                                      fontSize: "11px",
                                      color: "black",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {paymentDate}
                                  </div>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td
                                style={{
                                  ...styles.td,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                }}
                                onClick={() => toggleDetails(order.id)}
                              >
                                {expandedOrderId === order.id
                                  ? "Hide Details"
                                  : "Show Details"}
                              </td>
                              <td style={{ ...styles.td }}>
                                <select
                                  value={order.status}
                                  onChange={(e) =>
                                    changeOrderStatus(order.id, e.target.value)
                                  }
                                >
                                  <option value="in_progress">New order</option>
                                  <option value="shipped">In Transit</option>
                                  <option value="complete">Complete</option>
                                  <option value="canceled">Canceled</option>
                                  <option value="returned">Returned</option>
                                  <option value="reviewing">Reviewing</option>
                                </select>
                              </td>
                            </tr>
                            {expandedOrderId === order.id && (
                              <>
                                <tr
                                  style={{
                                    padding: 10,
                                    borderBottom: 0,
                                  }}
                                >
                                  <td
                                    colSpan={8}
                                    style={{
                                      ...styles.td,
                                      padding: 10,
                                      backgroundColor: "white",
                                      margin: 10,
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                      }}
                                    >
                                      <div
                                        style={{
                                          border: ".5px solid silver",
                                          padding: 20,
                                          borderRadius: 10,
                                          marginTop: 10,
                                          width: 500,
                                          marginRight: 20,
                                        }}
                                      >
                                        <b>Order By:</b>
                                        <table style={{ width: "100%" }}>
                                          <tbody>
                                            <tr>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                User UID:
                                              </td>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                {order?.user_id}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                Cart ID:
                                              </td>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                {order?.cart_id}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                Order ID:
                                              </td>
                                              <td
                                                style={{
                                                  opacity: 0.5,
                                                  fontSize: 12,
                                                }}
                                              >
                                                {order?.id}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                Email:
                                              </td>
                                              <td>
                                                {order?.buyerEmail || "N/A"}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                Name:
                                              </td>
                                              <td
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent:
                                                    "space-between",
                                                  gap: 8,
                                                }}
                                              >
                                                <span>
                                                  {
                                                    order?.shipmentAddress
                                                      ?.address_name
                                                  }
                                                </span>
                                                {order?.shipmentAddress
                                                  ?.address_name && (
                                                  <button
                                                    type="button"
                                                    style={{
                                                      fontSize: 12,
                                                      padding: "2px 8px",
                                                      borderRadius: 4,
                                                      border: "1px solid #ccc",
                                                      background: "#f5f5f5",
                                                      cursor: "pointer",
                                                      marginLeft: 8,
                                                    }}
                                                    onClick={() => {
                                                      navigator.clipboard.writeText(
                                                        order.shipmentAddress
                                                          .address_name,
                                                      );
                                                      toast.success(
                                                        "Name copied!",
                                                      );
                                                    }}
                                                  >
                                                    Copy
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                Address:
                                              </td>
                                              <td
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent:
                                                    "space-between",
                                                  gap: 8,
                                                }}
                                              >
                                                <span>
                                                  {order.shipmentAddress
                                                    .address_optional
                                                    ? correctAddress(
                                                        order.shipmentAddress
                                                          .address,
                                                        order.shipmentAddress
                                                          .address_optional,
                                                      )
                                                    : order?.shipmentAddress
                                                        ?.address}
                                                </span>
                                                {order?.shipmentAddress
                                                  ?.address && (
                                                  <button
                                                    type="button"
                                                    style={{
                                                      fontSize: 12,
                                                      padding: "2px 8px",
                                                      borderRadius: 4,
                                                      border: "1px solid #ccc",
                                                      background: "#f5f5f5",
                                                      cursor: "pointer",
                                                      marginLeft: 8,
                                                    }}
                                                    onClick={() => {
                                                      navigator.clipboard.writeText(
                                                        correctAddress(
                                                          order.shipmentAddress
                                                            .address,
                                                          order.shipmentAddress
                                                            .address_optional,
                                                        ),
                                                      );
                                                      toast.success(
                                                        "Address copied!",
                                                      );
                                                    }}
                                                  >
                                                    Copy
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                Order Type:
                                              </td>
                                              <td>
                                                {addressOrderCounts[
                                                  order.id
                                                ] !== undefined && (
                                                  <span
                                                    style={{
                                                      fontSize: 12,
                                                      color: "#478169",
                                                      background: "#e6f4ea",
                                                      borderRadius: 8,
                                                      padding: "2px 8px",
                                                      marginRight: 4,
                                                    }}
                                                  >
                                                    {addressOrderCounts[
                                                      order.id
                                                    ] === 1
                                                      ? "New"
                                                      : "Repeat " +
                                                        addressOrderCounts[
                                                          order.id
                                                        ]}
                                                  </span>
                                                )}
                                              </td>
                                            </tr>
                                            {order?.shipmentAddress
                                              ?.address_optional && (
                                              <tr>
                                                <td style={{ opacity: 0.5 }}>
                                                  Address (optional):
                                                </td>
                                                <td>
                                                  {
                                                    order?.shipmentAddress
                                                      ?.address_optional
                                                  }
                                                </td>
                                              </tr>
                                            )}
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                City:
                                              </td>
                                              <td>
                                                {order?.shipmentAddress?.city}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                State:
                                              </td>
                                              <td>
                                                {order?.shipmentAddress?.state}
                                              </td>
                                            </tr>
                                            <tr>
                                              <td style={{ opacity: 0.5 }}>
                                                Postal Code:
                                              </td>
                                              <td>
                                                {
                                                  order?.shipmentAddress
                                                    ?.postalCode
                                                }
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                      <div>
                                        <div
                                          style={{
                                            border: ".5px solid silver",
                                            padding: 20,
                                            borderRadius: 10,
                                            marginTop: 10,
                                            width: 500,
                                            marginRight: 20,
                                          }}
                                        >
                                          <div style={{ marginBottom: 10 }}>
                                            <b>Shipment Details:</b>
                                          </div>
                                          <div>
                                            <div
                                              style={{
                                                opacity: 0.5,
                                                fontSize: 12,
                                                marginBottom: 5,
                                              }}
                                            >
                                              Tracking Number:
                                            </div>
                                            <div>
                                              <input
                                                type="text"
                                                value={
                                                  trackingNumbers[order.id] ||
                                                  order.tracking_number ||
                                                  ""
                                                }
                                                onChange={(e) =>
                                                  handleTrackingNumberChange(
                                                    order.id,
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder="Enter tracking number"
                                                style={{
                                                  width: "100%",
                                                  padding: "5px",
                                                  border: "1px solid #ccc",
                                                  fontSize: 12,
                                                  borderRadius: "4px",
                                                }}
                                              />
                                            </div>
                                            <div>
                                              <button
                                                onClick={() =>
                                                  saveTrackingNumber(order.id)
                                                }
                                                style={{
                                                  marginTop: "5px",
                                                  backgroundColor: "#478169",
                                                  color: "white",
                                                  border: "none",
                                                  padding: "5px 10px",
                                                  cursor: "pointer",
                                                  borderRadius: "4px",
                                                  width: "100px",
                                                }}
                                              >
                                                Save
                                              </button>
                                            </div>

                                            {order.tracking_number && (
                                              <div>
                                                <div
                                                  style={{
                                                    opacity: 0.5,
                                                    fontSize: 12,
                                                  }}
                                                >
                                                  Tracking Link:
                                                </div>
                                                {order?.tracking_number && (
                                                  <div>
                                                    <a
                                                      href={getTrackingLink(
                                                        order.tracking_number,
                                                      )}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{
                                                        color: "blue",
                                                        textDecoration:
                                                          "underline",
                                                      }}
                                                    >
                                                      Track Shipment
                                                    </a>
                                                    <span
                                                      style={{
                                                        textDecoration: "none",
                                                        color: "gray",
                                                        paddingLeft: 5,
                                                      }}
                                                    >
                                                      ({order?.tracking_number})
                                                    </span>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            <table
                                              style={{
                                                width: "100%",
                                                marginTop: 10,
                                              }}
                                            >
                                              <tbody>
                                                {order?.shippingOption && (
                                                  <>
                                                    <tr>
                                                      <td
                                                        style={{ opacity: 0.5 }}
                                                      >
                                                        Shipping Method:
                                                      </td>
                                                      <td
                                                        style={{
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {
                                                          order.shippingOption
                                                            .name
                                                        }
                                                      </td>
                                                    </tr>
                                                    <tr>
                                                      <td
                                                        style={{ opacity: 0.5 }}
                                                      >
                                                        Delivery Time:
                                                      </td>
                                                      <td
                                                        style={{
                                                          textAlign: "center",
                                                        }}
                                                      >
                                                        {
                                                          order.shippingOption
                                                            .minimum_days
                                                        }{" "}
                                                        -{" "}
                                                        {
                                                          order.shippingOption
                                                            .maximum_days
                                                        }{" "}
                                                        days
                                                      </td>
                                                    </tr>
                                                  </>
                                                )}
                                                <tr>
                                                  <td style={{ opacity: 0.5 }}>
                                                    Shipping Cost:
                                                  </td>
                                                  <td
                                                    style={{
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    ${order?.shippingCost}
                                                  </td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                        <div
                                          style={{
                                            border: ".5px solid silver",
                                            padding: 20,
                                            borderRadius: 10,
                                            marginTop: 10,
                                            width: 500,
                                            marginRight: 20,
                                          }}
                                        >
                                          <div style={{ marginBottom: 10 }}>
                                            <b>Discount:</b>
                                          </div>
                                          <table style={{ width: "100%" }}>
                                            <tbody>
                                              <tr>
                                                <td style={{ opacity: 0.5 }}>
                                                  Discount Amount:
                                                </td>
                                                <td
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  ${order?.discountAmount}
                                                </td>
                                              </tr>
                                              <tr>
                                                <td style={{ opacity: 0.5 }}>
                                                  Discount Code:
                                                </td>
                                                <td
                                                  style={{
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {order?.discountCode
                                                    ? order?.discountCode
                                                    : "..."}
                                                </td>
                                              </tr>
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          border: ".5px solid silver",
                                          padding: 20,
                                          borderRadius: 10,
                                          marginTop: 10,
                                          flex: 1,
                                        }}
                                      >
                                        <div
                                          style={{ flex: 1, padding: "10px 0" }}
                                        >
                                          {console.log({ order })}
                                          <div
                                            style={{
                                              ...styles.buttonMail,
                                              width: 150,
                                            }}
                                            onClick={() =>
                                              downloadInvoice(order)
                                            }
                                          >
                                            Download Invoice
                                          </div>
                                        </div>
                                        <div
                                          style={{ flex: 1, padding: "10px 0" }}
                                        >
                                          <b>Sending Email:</b>
                                        </div>
                                        <div
                                          style={{
                                            height: "100%",
                                            flex: 1,
                                            padding: "10px 0",
                                          }}
                                        >
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "row",
                                              flexWrap: "wrap",
                                              gap: 8,
                                            }}
                                          >
                                            {order.status === "pending" && (
                                              <div
                                                style={{
                                                  ...styles.buttonMail,
                                                  width: 180,
                                                  backgroundColor: "#4CAF50",
                                                  color: "white",
                                                }}
                                                onClick={() =>
                                                  confirmManualPayment(order)
                                                }
                                              >
                                                Confirm Payment Received
                                              </div>
                                            )}
                                            <div
                                              style={{
                                                ...styles.buttonMail,
                                                width: 150,
                                              }}
                                              onClick={() =>
                                                sendDeliveryConfirmation(order)
                                              }
                                            >
                                              Package shipped
                                            </div>
                                            <div
                                              style={{
                                                display: "flex",
                                                textAlign: "right",
                                                flex: 1,
                                                justifyContent: "flex-end",
                                                padding: "0px 10px",
                                              }}
                                            ></div>
                                          </div>
                                          {/* Display Email Logs */}
                                          <div style={{ marginTop: "10px" }}>
                                            <h4>Email Logs</h4>
                                            {order.sendedMail &&
                                            order.sendedMail.length > 0 ? (
                                              <ul style={{ fontSize: 12 }}>
                                                {order.sendedMail.map(
                                                  (log, index) => (
                                                    <li key={index}>
                                                      <strong>
                                                        {log.mail_type}
                                                      </strong>{" "}
                                                      -{" "}
                                                      {moment(log.date).format(
                                                        "MM/DD/YYYY, h:mm A",
                                                      )}
                                                    </li>
                                                  ),
                                                )}
                                              </ul>
                                            ) : (
                                              <p>
                                                No emails sent yet for this
                                                order.
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                                <tr
                                  style={{
                                    backgroundColor: "white",
                                    borderBottom: 0,
                                    borderTop: 0,
                                  }}
                                >
                                  <td
                                    colSpan={8}
                                    style={{
                                      ...styles.td,
                                      padding: 10,
                                    }}
                                  >
                                    <b>Products:</b>
                                    <div
                                      style={{
                                        border: ".5px solid silver",
                                        padding: 20,
                                        borderRadius: 10,
                                        marginTop: 10,
                                      }}
                                    >
                                      <table style={styles.itemsTable}>
                                        <thead>
                                          <tr>
                                            <th style={styles.itemsTh}>No.</th>
                                            <th style={styles.itemsTh}>
                                              Product Name
                                            </th>
                                            <th style={styles.itemsTh}>
                                              Quantity
                                            </th>
                                            <th style={styles.itemsTh}>
                                              Price
                                            </th>
                                            <th style={styles.itemsTh}>
                                              Total
                                            </th>
                                            <th style={styles.itemsTh}>
                                              Sending Status
                                            </th>
                                            <th style={styles.itemsTh}>
                                              Notes
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {order.items.map((product, index) => {
                                            const sendingStatusOptions = [
                                              {
                                                value: "not_sent",
                                                label: "Not sent",
                                              },
                                              {
                                                value: "pick_pack",
                                                label: "Pick/pack",
                                              },
                                              {
                                                value: "labeling",
                                                label: "Labeling",
                                              },
                                              { value: "sent", label: "Sent" },
                                              {
                                                value: "delivered",
                                                label: "Delivered",
                                              },
                                              {
                                                value: "returned",
                                                label: "Returned",
                                              },
                                              {
                                                value: "canceled",
                                                label: "Canceled",
                                              },
                                              {
                                                value: "refunded",
                                                label: "Refunded",
                                              },
                                              {
                                                value: "backorder",
                                                label: "Backorder",
                                              },
                                            ];

                                            return (
                                              <tr key={index}>
                                                <td style={styles.itemsTd}>
                                                  <b>{index + 1}</b>
                                                </td>
                                                <td style={styles.itemsTd}>
                                                  {product.product_name}
                                                </td>
                                                <td
                                                  style={{
                                                    ...styles.itemsTd,
                                                    ...(product.quantity > 1
                                                      ? styles.highlighted
                                                      : {}),
                                                    textAlign: "center",
                                                  }}
                                                >
                                                  {product.quantity}
                                                </td>
                                                <td style={styles.itemsTd}>
                                                  $
                                                  {product?.final_price &&
                                                    Number(
                                                      product.final_price,
                                                    ).toFixed(2)}
                                                </td>
                                                <td style={styles.itemsTd}>
                                                  $
                                                  {product?.final_price &&
                                                    Number(
                                                      product.quantity *
                                                        product.final_price,
                                                    ).toFixed(2)}
                                                </td>
                                                <td style={styles.itemsTd}>
                                                  <select
                                                    value={
                                                      product.sending_status
                                                    }
                                                    onChange={(e) =>
                                                      changeItemStatus(
                                                        product.id,
                                                        e.target.value,
                                                      )
                                                    }
                                                  >
                                                    {sendingStatusOptions.map(
                                                      (option) => (
                                                        <option
                                                          key={option.value}
                                                          value={option.value}
                                                        >
                                                          {option.label}
                                                        </option>
                                                      ),
                                                    )}
                                                  </select>
                                                </td>
                                                {/* Notes input field */}
                                                <td
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    marginTop: 5,
                                                  }}
                                                >
                                                  <input
                                                    type="text"
                                                    value={product.notes || ""} // Bind the input to the notes value in the product
                                                    onChange={(e) =>
                                                      handleNotesChange(
                                                        product.id,
                                                        e.target.value,
                                                      )
                                                    }
                                                    placeholder="Enter notes here"
                                                    style={styles.notesInput}
                                                  />
                                                  <button
                                                    onClick={() =>
                                                      saveProductNotes(
                                                        order.id,
                                                        product.id,
                                                        product.notes,
                                                      )
                                                    } // Pass order ID, product ID, and notes
                                                    style={
                                                      styles.saveNotesButton
                                                    }
                                                  >
                                                    Save Notes
                                                  </button>
                                                </td>
                                              </tr>
                                            );
                                          })}
                                          <tr>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td
                                              style={{
                                                fontWeight: "bold",
                                                textAlign: "center",
                                              }}
                                            >
                                              Total:
                                            </td>
                                            <td>
                                              $
                                              {order.items
                                                .reduce(
                                                  (total, item) =>
                                                    total +
                                                    (item.final_price *
                                                      item.quantity || 0),
                                                  0,
                                                )
                                                .toFixed(2)}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                                <tr
                                  style={{
                                    backgroundColor: "white",
                                    border: "1px solid #808080",
                                    borderTop: 0,
                                  }}
                                >
                                  <td
                                    colSpan={6}
                                    style={{
                                      ...styles.td,
                                      padding: 10,
                                      textAlign: "right",
                                      display: "none",
                                    }}
                                  >
                                    <button type="submit" style={styles.button}>
                                      Save Changes
                                    </button>
                                  </td>
                                </tr>
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div
              style={{ background: "#fafafa", borderTop: "1px solid silver" }}
            >
              <TablePagination
                component="div"
                count={filteredOrders.length} // Total count of filtered orders
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]} // Options for rows per page
              />
            </div>
          </form>
        </>
      )}
    </div>
  );
};

const styles = {
  tabButton: {
    backgroundColor: "white",
    cursor: "pointer",
    borderRadius: "5px 5px 0 0",
    fontSize: 12,
  },
  //
  form: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #ccc",
    backgroundColor: "#f9f9f9",
    width: "100%",
  },
  formElement: {
    display: "flex",
    flexDirection: "column",
    gap: 0,
    marginBottom: 10,
    width: 100,
    marginRight: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px 15px",
    fontSize: 16,
    border: ".5px solid #ccc",
    borderRadius: 5,
    textAlign: "left",
    backgroundColor: "rgba(255,255,255,.8)",
  },
  button: {
    padding: "0px",
    width: "120px",
    backgroundColor: "#478169",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: "14px",
    height: "50px",
  },
  //
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    paddingTop: 10,
    paddingBottom: 10,
    textAlign: "left",
    borderBottom: ".5px solid silver",
    marginBottom: 20,
    fontSize: 12,
    paddingLeft: 10,
  },
  td: {
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 10,
    textAlign: "left",
  },
  itemsTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  itemsTh: {
    paddingTop: 10,
    paddingBottom: 10,
    textAlign: "left",
    borderBottom: ".5px solid silver",
    marginBottom: 20,
    fontSize: 12,
  },
  itemsTd: {
    paddingTop: 10,
    paddingBottom: 10,
    textAlign: "left",
  },
  highlighted: {
    backgroundColor: "greenyellow",
    fontWeight: "bold",
  },
  notesInput: {
    width: "100%",
    padding: "5px",
    border: "1px solid #ccc",
    borderRadius: "4px",
  },
  saveNotesButton: {
    backgroundColor: "#478169",
    color: "white",
    border: "none",
    padding: "5px 10px",
    cursor: "pointer",
    borderRadius: "4px",
    width: "100px",
    marginLeft: 10,
  },
  //
  buttonMail: {
    backgroundColor: "#fafafa",
    color: "black",
    border: "none",
    padding: "5px 10px",
    cursor: "pointer",
    borderRadius: "4px",
    width: "100px",
    marginRight: 10,
    border: "1px solid #ccc",
    textAlign: "center",
  },
};

export default Orders;
